/**
 * Calculation Details Drawer
 *
 * Right-side drawer view of an activity. Composes the same discrete
 * sub-views the full-page detail uses (`components/activity-detail/*`),
 * laid out as a stacked flex column inside three tabs.
 */

import { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Sidebar } from '@g17eco/molecules/sidebar';
import { TabNav, type TabItem } from '@g17eco/ui-modern/components/ui/tab-nav';
import { ConfirmationModal } from '@g17eco/molecules/confirm-modal';
import { ExternalLink } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { generateUrl } from '@routes/util';
import { useGetCalculationHistoryQuery, useRevertCalculationVersionMutation } from '../api/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { ActivityRecord } from '../types/emissions';
import { emissionsTrackerRoutes } from '../routes';
import { UnitToggle } from './UnitToggle';
import { EvidenceTab } from './EvidenceTab';
import {
  ActivityBiogenicCard,
  ActivityCo2eHeadline,
  ActivityGasBreakdownCard,
  ActivityInputTable,
  ActivitySupplementaryCard,
  hasGasBreakdownData,
  hasSupplementaryData,
  type SummaryActivityData,
} from './activity-detail/ActivitySummaryView';
import {
  ActivityFactorView,
  ActivityMethodologyView,
  ActivityReferencesView,
  type CalculationActivityData,
} from './activity-detail/ActivityCalculationView';
import { ActivityHistoryView } from './activity-detail/ActivityHistoryView';
import { ActivityNoticesPanel } from './activity-detail/ActivityNoticesPanel';

interface CalculationDetailsDrawerProps {
  isOpen: boolean;
  toggle: () => void;
  initiativeId: string;
  activityId: string | null;
  activityData?: SummaryActivityData &
    CalculationActivityData &
    Pick<ActivityRecord, 'calculationNotices'>;
  onRecalculate?: (activityId: string, initiativeId: string) => void;
  isRecalculating?: boolean;
  reportingLevelLabel?: React.ReactNode;
}

type TabId = 'summary' | 'calculation' | 'history';

// Evidence lives inline at the bottom of the Summary tab rather than a
// dedicated tab — the list is short (usually 0–3 files) and readers
// scan inputs and supporting docs together.
const TABS: TabItem<TabId>[] = [
  { key: 'summary', label: 'Summary', icon: <i className='fa fa-info-circle' /> },
  { key: 'calculation', label: 'Calculation', icon: <i className='fa fa-calculator' /> },
  { key: 'history', label: 'History', icon: <i className='fa fa-history' /> },
];

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h5 className='text-ThemeTextLight tw:mt-4 tw:mb-2 tw:text-uppercase fw-semibold'>{children}</h5>
);

export const CalculationDetailsDrawer = ({
  isOpen,
  toggle,
  initiativeId,
  activityId,
  activityData,
  onRecalculate,
  isRecalculating = false,
  reportingLevelLabel,
}: CalculationDetailsDrawerProps) => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [revertVersion, setRevertVersion] = useState<number | null>(null);

  const { settings } = useEmissionsFormatter({ initiativeId });
  const [revertCalculation, { isLoading: isReverting }] = useRevertCalculationVersionMutation();

  const {
    currentData: calculationHistory,
    isLoading,
    isFetching,
    isError,
  } = useGetCalculationHistoryQuery(
    { initiativeId, activityId: activityId ?? '' },
    { skip: !initiativeId || !activityId },
  );

  const currentCalculation = calculationHistory?.[0];

  const defaultToTonnes = settings.displayUnit !== 'kgCO2e';
  const [showInTonnes, setShowInTonnes] = useState(defaultToTonnes);

  // Sync toggle with org settings once they load (settings may be fetched after initial render)
  useEffect(() => {
    setShowInTonnes(settings.displayUnit !== 'kgCO2e');
  }, [settings.displayUnit]);

  const handleRevert = async () => {
    if (!activityId || revertVersion === null) return;
    await revertCalculation({ initiativeId, activityId, targetVersion: revertVersion }).unwrap();
    setRevertVersion(null);
  };

  const handleOpenFullPage = () => {
    if (!activityId) return;
    toggle();
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_DETAIL, { initiativeId, activityId }));
  };

  const formatProps = { showInTonnes, decimalPlaces: settings.decimalPlaces };

  const renderContent = () => {
    if (isLoading || isFetching) {
      return (
        <div className='py-5 text-center'>
          <Spinner color='success' />
          <p className='text-ThemeTextLight mt-3 mb-0'>Loading calculation details...</p>
        </div>
      );
    }

    if (isError) {
      return (
        <div className='py-5 text-center text-ThemeTextLight'>
          <i className='fa fa-exclamation-circle fa-3x mb-3 text-ThemeDangerMedium opacity-75' />
          <p className='mb-1 text-ThemeTextDark fw-medium'>Failed to load calculation details</p>
          <p className='mb-0'>Please close and try again.</p>
        </div>
      );
    }

    if (!currentCalculation) {
      return (
        <div className='py-5 text-center text-ThemeTextLight'>
          <i className='fa fa-calculator fa-3x mb-3 opacity-25' />
          <p className='mb-1'>No calculation found</p>
          <p className='mb-0'>This activity has not been calculated yet.</p>
        </div>
      );
    }

    return (
      <>
        <div className='tw:flex tw:items-center tw:justify-between tw:gap-2 tw:mb-2'>
          {activityId && (
            <div className='tw:flex tw:gap-2'>
              <Button variant='secondary' size='xs' onClick={handleOpenFullPage}>
                <ExternalLink className='tw:size-3.5 tw:mr-1' />
                View details
              </Button>
            </div>
          )}
        </div>

        <TabNav variant='links' fullWidth tabs={TABS} activeTab={activeTab} onChange={setActiveTab} className='tw:mb-1' />

        {activeTab === 'summary' && (
          <div className='tw:flex tw:flex-col tw:gap-4 tw:py-3'>
            <div className='tw:flex tw:justify-end tw:gap-2'>
              <UnitToggle showInTonnes={showInTonnes} onChange={setShowInTonnes} />
            </div>
            <ActivityCo2eHeadline calculation={currentCalculation} {...formatProps} />
            {hasGasBreakdownData(currentCalculation) && (
              <ActivityGasBreakdownCard calculation={currentCalculation} {...formatProps} />
            )}
            {hasSupplementaryData(currentCalculation) && (
              <ActivitySupplementaryCard calculation={currentCalculation} {...formatProps} />
            )}
            <ActivityBiogenicCard calculation={currentCalculation} {...formatProps} />
            {activityData && (
              <ActivityInputTable activityData={activityData} reportingLevelLabel={reportingLevelLabel} />
            )}
            {activityId && (
              <div>
                <SectionHeading>Evidence</SectionHeading>
                <EvidenceTab initiativeId={initiativeId} activityId={activityId} />
              </div>
            )}
            {currentCalculation.notices && currentCalculation.notices.length > 0 && (
              <div>
                <SectionHeading>Notices</SectionHeading>
                <ActivityNoticesPanel notices={currentCalculation.notices} />
              </div>
            )}
          </div>
        )}

        {activeTab === 'calculation' && (
          <div className='tw:flex tw:flex-col tw:gap-4 tw:py-3'>
            <ActivityFactorView
              calculation={currentCalculation}
              activityData={activityData}
              initiativeId={initiativeId}
              activityId={activityId ?? ''}
            />
            {onRecalculate && activityId && (
              <div className='tw:flex tw:justify-end'>
                <Button
                  variant='secondary'
                  size='xs'
                  onClick={() => onRecalculate(activityId, initiativeId)}
                  disabled={isRecalculating}
                >
                  <i className='fa fa-refresh tw:mr-1' />
                  {isRecalculating ? 'Recalculating...' : 'Recalculate'}
                </Button>
              </div>
            )}
            <div>
              <SectionHeading>Methodology</SectionHeading>
              <ActivityMethodologyView calculation={currentCalculation} activityData={activityData} />
            </div>
            <div>
              <SectionHeading>References &amp; Standards</SectionHeading>
              <ActivityReferencesView calculation={currentCalculation} />
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className='tw:flex tw:flex-col tw:gap-4 tw:py-3'>
            <ActivityHistoryView
              calculations={calculationHistory ?? []}
              isLoading={isFetching}
              showInTonnes={showInTonnes}
              decimalPlaces={settings.decimalPlaces}
              onRevert={(version) => setRevertVersion(version)}
              isReverting={isReverting}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Sidebar isOpen={isOpen} toggle={toggle} header='Calculation Details' direction='end' scrollable>
        {renderContent()}
      </Sidebar>
      <ConfirmationModal
        title='Revert Calculation'
        isOpen={revertVersion !== null}
        handleCancel={() => setRevertVersion(null)}
        content={
          <p className='mb-0'>
            This will create a new version with the data from version {revertVersion}.
            The current calculation will be preserved in history.
          </p>
        }
        confirmButton={
          <Button onClick={handleRevert} disabled={isReverting}>
            {isReverting ? 'Reverting...' : 'Revert'}
          </Button>
        }
      />
    </>
  );
};
